import { Autocomplete, Grid, OutlinedInput, TextField } from '@mui/material'
import { SCOPES } from 'components'
import { DeckMappDialog } from 'components/DeckMappDialog'
import { useCurrentUser } from 'hooks'
import { useSnackbar } from 'notistack'
import { adminUpdateUserAttributes, createUser } from 'pages/admin/common/adminDatastore'
import { addOrUpdateUserRole, removeUserRole } from 'pages/admin/common/data'
import { useMemo, useState } from 'react'

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

const normalizeUser = (data) =>
  data
    ? {
        companyNames: data.CompanyIds || data.companyNames,
        email: data.Email || data.email,
        firstName: data.FirstName || data.firstName,
        lastName: data.LastName || data.lastName,
        roles: data.RoleIds || data.roles
      }
    : { companyNames: [], email: '', firstName: '', lastName: '', roles: [] }

const UserForm = ({ customers, roles, user, errors, setField, setError, canEditEmail = true }) => {
  const { currentUser } = useCurrentUser()

  const writeableCustomers = useMemo(
    () => currentUser.getCustomersForScope(SCOPES.userManagement.Write),
    [currentUser]
  )

  const filteredCustomers = useMemo(
    () => customers.filter((c) => currentUser.isAdmin || writeableCustomers.includes(c.id)),
    [customers, currentUser.isAdmin, writeableCustomers]
  )

  const filteredRoles = useMemo(
    () => (user.companyNames.length ? roles.filter((r) => r.Enabled && user.companyNames.includes(r.CustomerId)) : []),
    [roles, user.companyNames]
  )

  const selectedCustomers = useMemo(
    () => filteredCustomers.filter((c) => user.companyNames.includes(c.id)),
    [filteredCustomers, user.companyNames]
  )

  const selectedRoles = useMemo(
    () => filteredRoles.filter((r) => user.roles.includes(r.id)),
    [filteredRoles, user.roles]
  )

  return (
    <Grid container flexDirection="column" spacing={1} alignItems="center">
      <Grid width="100%" item>
        <Autocomplete
          multiple
          options={filteredCustomers}
          getOptionLabel={(option) => option.Name}
          value={selectedCustomers}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => {
            setError('companyNames', undefined)
            const companyNames = newValue.map((c) => c.id)
            const retainedRoles = roles
              .filter((role) => companyNames.includes(role.CustomerId) && user.roles.includes(role.id))
              .map((role) => role.id)
            setField('companyNames', companyNames)
            setField('roles', retainedRoles)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select companies"
              color="secondary"
              error={!!errors.companyNames}
              helperText={errors.companyNames}
            />
          )}
        />
      </Grid>

      <Grid width="100%" item>
        <OutlinedInput
          error={!!errors.email}
          value={user.email}
          placeholder="Email address"
          color="secondary"
          disabled={!canEditEmail}
          fullWidth
          onChange={({ target: { value } }) => {
            if (errors.email) setError('email', EMAIL_REGEX.test(value) ? undefined : errors.email)
            setField('email', value)
          }}
        />
      </Grid>

      <Grid width="100%" item>
        <OutlinedInput
          value={user.firstName}
          error={!!errors.firstName}
          placeholder="First name"
          color="secondary"
          fullWidth
          onChange={({ target: { value } }) => {
            setError('firstName', undefined)
            setField('firstName', value)
          }}
        />
      </Grid>

      <Grid width="100%" item>
        <OutlinedInput
          value={user.lastName}
          error={!!errors.lastName}
          placeholder="Last name"
          color="secondary"
          fullWidth
          onChange={({ target: { value } }) => {
            setError('lastName', undefined)
            setField('lastName', value)
          }}
        />
      </Grid>

      <Grid width="100%" item>
        <Autocomplete
          multiple
          options={filteredRoles}
          getOptionLabel={(option) => option.Name}
          value={selectedRoles}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={!user.companyNames.length}
          noOptionsText="No roles assigned to selected customer"
          onChange={(_, newValue) => setField('roles', newValue.map((r) => r.id))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select roles"
              color="secondary"
              error={!!errors.roles}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}

export const AddUser = ({ refetch, closeModal, data, roles, customers }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { currentUser } = useCurrentUser()
  const [user, setUser] = useState(() => normalizeUser(data))
  const [errors, setErrors] = useState({})
  const { enqueueSnackbar } = useSnackbar()

  const setField = (field, value) => setUser((prev) => ({ ...prev, [field]: value }))
  const setError = (field, msg) => setErrors((prev) => ({ ...prev, [field]: msg }))

  const validate = () => {
    const newErrors = {}
    if (!user.email.trim()) {
      newErrors.email = 'Email address of user is required'
    } else if (!EMAIL_REGEX.test(user.email)) {
      newErrors.email = 'Email address entered is invalid'
    }
    if (!user.companyNames.length) newErrors.companyNames = 'Select company'
    if (!user.firstName.trim()) newErrors.firstName = 'Enter first name of user'
    if (!user.lastName.trim()) newErrors.lastName = 'Enter last name of user'

    if (Object.keys(newErrors).length) {
      setErrors((prev) => ({ ...prev, ...newErrors }))
      return false
    }
    return true
  }

  const createNewUser = async () => {
    enqueueSnackbar('Adding user ...', { autoHideDuration: 2000 })

    const newRoles = roles.filter((r) => user.roles.includes(r.id))
    
    const sites = JSON.stringify([...new Set(newRoles.flatMap((r) => r.SiteIds))])
    const companyNames = JSON.stringify(user.companyNames)
    const groups = newRoles.map((role) => role.Group)
    const newUser = await createUser(
      user.email,
      user.firstName,
      user.lastName,
      companyNames,
      sites,
      groups,
      currentUser.accessToken
    )

    await Promise.all(
      newRoles.map((role) =>
        addOrUpdateUserRole({
          CognitoId: newUser?.sandvikUserId ?? newUser?.User?.Username,
          CustomerId: role.CustomerId,
          RoleId: role.id
        })
      )
    )

    await refetch()
    enqueueSnackbar('User successfully added', { variant: 'success' })
    closeModal()
  }

  const editUser = async () => {
    const original = {
      companyNames: data.CompanyIds,
      email: data.Email,
      firstName: data.FirstName,
      lastName: data.LastName,
      roles: data.RoleIds
    }

    if (JSON.stringify(user) === JSON.stringify(original)) {
      enqueueSnackbar('User info has not changed')
      return
    }

    enqueueSnackbar('Updating user ...')

    const originalCompanies = data.CompanyIds ?? []
    const newCompanies = user.companyNames ?? []
    const originalRoles = roles.filter((r) => data.RoleIds.includes(r.id))
    const newRoleIds = user.roles.filter((id) => !data.RoleIds.includes(id))
    const rolesToRemove = originalRoles.filter((r) => !user.roles.includes(r.id))
    const newRoles = roles.filter((r) => newRoleIds.includes(r.id))
    const allRoles = roles.filter((r) => user.roles.includes(r.id))

    const originalGroups = originalRoles.map((r) => r.Group)
    const newGroups = allRoles.map((r) => r.Group)
    const groupsToRemove = originalGroups.filter((group) => !newGroups.includes(group))
    const groupsToAdd = newGroups.filter((group) => !originalGroups.includes(group))

    const sites = JSON.stringify([...new Set(newRoles.flatMap((r) => r.SiteIds).filter(Boolean))])
    const companiesToRemove = originalCompanies.filter((company) => !newCompanies.includes(company))
    const companyNames = JSON.stringify([
      ...new Set(
        [...newRoles.map((r) => r.CustomerId), ...originalCompanies, ...newCompanies]
          .filter(Boolean)
          .filter((c) => !companiesToRemove.includes(c))
      )
    ])

    try {
      await adminUpdateUserAttributes(
        data.Username,
        user.firstName,
        user.lastName,
        companyNames,
        sites,
        groupsToAdd,
        groupsToRemove
      )

      await Promise.all([
        ...rolesToRemove.map((role) => removeUserRole(data.SandvikUserId, role.id, currentUser.accessToken)),
        ...newRoles.map((role) =>
          addOrUpdateUserRole({ CognitoId: data.SandvikUserId, CustomerId: role.CustomerId, RoleId: role.id })
        )
      ])

      await refetch()
      enqueueSnackbar('User Item updated successfully', { variant: 'success' })
      closeModal()
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message ?? 'An error occurred', { variant: 'error' })
    }
  }

  const onAddUserPressed = async () => {
    if (!validate()) return
    setIsLoading(true)
    try {
      if (data?.SandvikUserId) {
        await editUser()
      } else {
        await createNewUser()
      }
    } catch (e) {
      if (e.response?.data?.message) {
        enqueueSnackbar(e.response?.data?.message, { variant: 'error' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <DeckMappDialog
      onCancel={closeModal}
      saveButtonProps={{
        title: !data?.SandvikUserId ? 'Add user' : 'Edit User',
        onClick: onAddUserPressed
      }}
      isLoading={isLoading}
      open
      title={!data?.SandvikUserId ? 'Add user' : 'Edit User'}>
      <UserForm
        customers={customers}
        roles={roles}
        user={user}
        errors={errors}
        setField={setField}
        setError={setError}
        canEditEmail={!data?.SandvikUserId}
      />
    </DeckMappDialog>
  )
}
